type AppPath = "starter" | "builder" | "master" | "pro";

interface Props {
  onComplete: (path: AppPath) => void;
  onSeeAll: () => void;
}

const QUESTIONS = [
  {
    id: "age",
    question: "First up — how old are you?",
    emoji: "🎂",
    subtitle: "We'll use this to find your perfect starting point.",
    options: [
      { value: "under12", label: "Under 12",    emoji: "⭐", desc: "Elementary school"  },
      { value: "12to15",  label: "12 – 15",     emoji: "🚀", desc: "Middle school"      },
      { value: "16to18",  label: "16 – 18",     emoji: "🏆", desc: "High school"        },
      { value: "18plus",  label: "18 or older", emoji: "💼", desc: "Young adult"        },
    ],
  },
  {
    id: "goal",
    question: "What's your biggest money goal?",
    emoji: "🎯",
    subtitle: "Pick the one that excites you most right now.",
    options: [
      { value: "save_fun",   label: "Save up for something I want",        emoji: "🎮", desc: "Games, clothes, gadgets"       },
      { value: "understand", label: "Actually understand how money works",  emoji: "📚", desc: "The basics no one teaches you" },
      { value: "invest",     label: "Start investing & grow my wealth",     emoji: "📈", desc: "Stocks, funds, compound growth" },
      { value: "plan",       label: "Build a real budget & financial plan", emoji: "📋", desc: "Track spending, hit goals"      },
    ],
  },
  {
    id: "knowledge",
    question: "How would you describe yourself?",
    emoji: "🧠",
    subtitle: "Be honest — there's no wrong answer here.",
    options: [
      { value: "beginner",     label: "Total beginner",              emoji: "🌱", desc: "Money is still a mystery to me" },
      { value: "basics",       label: "I know some basics",          emoji: "📖", desc: "I get earning and saving"       },
      { value: "intermediate", label: "Comfortable with the basics", emoji: "💡", desc: "Ready for the next level"       },
      { value: "advanced",     label: "Ready for advanced topics",   emoji: "🔥", desc: "Bring on the hard stuff"        },
    ],
  },
];

const PATH_INFO: Record<AppPath, { name: string; emoji: string; bg: string; btnColor: string }> = {
  starter: { name: "Money Starter", emoji: "⭐", bg: "linear-gradient(135deg,#22c55e,#06b6d4)",  btnColor: "#16a34a" },
  builder: { name: "Money Builder", emoji: "🚀", bg: "linear-gradient(135deg,#f97316,#f59e0b)",  btnColor: "#ea580c" },
  master:  { name: "Money Master",  emoji: "🏆", bg: "linear-gradient(135deg,#6366f1,#8b5cf6)",  btnColor: "#4f46e5" },
  pro:     { name: "Pro Mode",      emoji: "💼", bg: "linear-gradient(135deg,#0f172a,#1e3a5f)",  btnColor: "#0ea5e9" },
};

function getRecommendation(answers: Record<string, string>): AppPath {
  const { age, goal, knowledge } = answers;
  if (age === "18plus") return "pro";
  if (age === "16to18") {
    if (knowledge === "beginner") return "builder";
    return "master";
  }
  if (age === "12to15") {
    if (knowledge === "advanced" || goal === "invest" || goal === "plan") return "master";
    return "builder";
  }
  // under 12
  if (knowledge === "advanced" || goal === "invest") return "builder";
  return "starter";
}

function getPersonalizedMessage(answers: Record<string, string>, path: AppPath): string {
  const { goal, knowledge } = answers;
  if (path === "pro") {
    return goal === "plan"
      ? "You're ready to build a real financial plan. Pro Mode gives you a budget tracker, investment roadmap, and goal-setting tools."
      : "You've got the age and mindset for the full experience. Pro Mode has everything you need to start building serious wealth.";
  }
  if (path === "master") {
    return knowledge === "advanced"
      ? "You're already ahead — let's put that knowledge to work with bonds, Roth IRAs, taxes, and real investment strategy."
      : "You're ready to go deep. Money Master covers the advanced topics most people only learn the hard way.";
  }
  if (path === "builder") {
    return goal === "invest"
      ? "Nice goal! Money Builder is where you'll learn how the stock market actually works — and why index funds beat most investors."
      : "Great starting point. You'll learn how to make money grow, use credit smartly, and build real wealth habits.";
  }
  return "Everyone starts somewhere — and starting early is the biggest money advantage there is. Let's build your foundation!";
}

import { useState } from "react";

export default function OnboardingQuestionnaire({ onComplete, onSeeAll }: Props) {
  const [step, setStep]       = useState<number | "result">(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [leaving, setLeaving]   = useState(false);

  const stepNum       = step as number;
  const q             = step !== "result" ? QUESTIONS[stepNum] : null;
  const recommendation = step === "result" ? getRecommendation(answers) : null;

  function handleNext() {
    if (!selected) return;
    const newAnswers = { ...answers, [QUESTIONS[stepNum].id]: selected };
    setLeaving(true);
    setTimeout(() => {
      setLeaving(false);
      setAnswers(newAnswers);
      setSelected(null);
      if (stepNum < QUESTIONS.length - 1) {
        setStep(stepNum + 1);
      } else {
        setStep("result");
      }
    }, 180);
  }

  /* ── RESULT SCREEN ── */
  if (step === "result" && recommendation) {
    const info    = PATH_INFO[recommendation];
    const message = getPersonalizedMessage(answers, recommendation);
    return (
      <div style={{
        maxWidth: 480, margin: "0 auto", minHeight: "100vh",
        background: "linear-gradient(160deg,#fff7ed 0%,#eff6ff 100%)",
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "40px 20px 48px",
      }}>
        <div style={{ fontSize: 60, marginBottom: 14, animation: "pop 0.5s ease both" }}>🎉</div>
        <div style={{
          fontFamily: "'Fredoka One', cursive", fontSize: 28,
          color: "#1e293b", textAlign: "center", marginBottom: 8,
        }}>
          We found your path!
        </div>
        <div style={{
          fontSize: 14, color: "#64748b", fontWeight: 600,
          textAlign: "center", marginBottom: 32, lineHeight: 1.5, padding: "0 8px",
        }}>
          Based on your answers, here's where you should start:
        </div>

        <div style={{
          width: "100%", background: info.bg, borderRadius: 24,
          padding: "24px 22px 20px", marginBottom: 20,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          animation: "slideUp 0.4s ease 0.1s both",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 48, lineHeight: 1 }}>{info.emoji}</div>
            <div>
              <div style={{
                fontFamily: "'Fredoka One', cursive", fontSize: 26,
                color: "#fff", lineHeight: 1, marginBottom: 6,
              }}>
                {info.name}
              </div>
              <div style={{
                background: "rgba(255,255,255,0.2)", borderRadius: 20,
                padding: "3px 11px", fontSize: 11, fontWeight: 800,
                color: "#fff", display: "inline-block",
              }}>
                Recommended for you ✓
              </div>
            </div>
          </div>
          <div style={{
            fontSize: 13, color: "rgba(255,255,255,0.92)",
            fontWeight: 600, lineHeight: 1.55, marginBottom: 18,
          }}>
            {message}
          </div>
          <button
            onClick={() => onComplete(recommendation)}
            className="btn-bounce"
            style={{
              width: "100%", background: "#fff", color: info.btnColor,
              border: "none", borderRadius: 40, padding: "14px 18px",
              fontSize: 16, fontWeight: 900, cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}
          >
            Let's go! →
          </button>
        </div>

        <button
          onClick={onSeeAll}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 13, color: "#94a3b8", fontWeight: 700,
            padding: "8px", textDecoration: "underline",
          }}
        >
          See all paths instead
        </button>
      </div>
    );
  }

  if (!q) return null;

  /* ── QUESTION SCREEN ── */
  return (
    <div style={{
      maxWidth: 480, margin: "0 auto", minHeight: "100vh",
      background: "linear-gradient(160deg,#fff7ed 0%,#eff6ff 100%)",
      display: "flex", flexDirection: "column",
      padding: "28px 20px 110px",
    }}>
      {/* Progress bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 32 }}>
        {QUESTIONS.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 6, borderRadius: 3,
            background: i <= stepNum ? "#f97316" : "#e2e8f0",
            transition: "background 0.3s",
          }} />
        ))}
      </div>

      {/* Question */}
      <div style={{
        opacity: leaving ? 0 : 1,
        transform: leaving ? "translateX(16px)" : "translateX(0)",
        transition: "opacity 0.18s, transform 0.18s",
      }}>
        <div style={{ fontSize: 44, textAlign: "center", marginBottom: 12 }}>{q.emoji}</div>
        <div style={{
          fontFamily: "'Fredoka One', cursive", fontSize: 24,
          color: "#1e293b", textAlign: "center", lineHeight: 1.25, marginBottom: 8,
        }}>
          {q.question}
        </div>
        <div style={{
          fontSize: 13, color: "#64748b", fontWeight: 600,
          textAlign: "center", marginBottom: 26,
        }}>
          {q.subtitle}
        </div>

        {/* Answer cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.options.map(opt => {
            const on = selected === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelected(opt.value)}
                className="btn-bounce"
                style={{
                  width: "100%", background: on ? "#fff7ed" : "#fff",
                  border: `2px solid ${on ? "#f97316" : "#e2e8f0"}`,
                  borderRadius: 16, padding: "14px 16px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 14, textAlign: "left",
                  boxShadow: on ? "0 4px 16px rgba(249,115,22,0.18)" : "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
                  minHeight: 64,
                }}
              >
                <div style={{ fontSize: 30, lineHeight: 1, flexShrink: 0 }}>{opt.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: on ? "#f97316" : "#1e293b" }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginTop: 2 }}>
                    {opt.desc}
                  </div>
                </div>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  background: on ? "#f97316" : "#e2e8f0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}>
                  {on && <span style={{ fontSize: 12, color: "#fff", fontWeight: 900 }}>✓</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next button — fixed at bottom, fades in after selection */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 480, padding: "16px 20px 32px",
        background: "linear-gradient(to top, #fff7ed 65%, transparent)",
        opacity: selected ? 1 : 0,
        pointerEvents: selected ? "auto" : "none",
        transition: "opacity 0.2s",
      }}>
        <button
          onClick={handleNext}
          className="btn-bounce"
          style={{
            width: "100%",
            background: "linear-gradient(135deg,#f97316,#ec4899)",
            border: "none", borderRadius: 40, padding: "15px 18px",
            fontSize: 16, fontWeight: 900, color: "#fff", cursor: "pointer",
            boxShadow: "0 4px 18px rgba(249,115,22,0.35)",
          }}
        >
          {stepNum < QUESTIONS.length - 1 ? "Next →" : "See my recommendation →"}
        </button>
      </div>
    </div>
  );
}
